import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  createCustomerTag,
  deleteCustomerTag,
  updateCustomerTag,
  useCustomerTags,
  type CustomerTag,
} from "@/lib/customer-tags";
import { Drawer, DrawerHeader, DrawerBody, DrawerFooter } from "./Drawer";
import { ConfirmDialog } from "./ConfirmDialog";
import { EmptyState } from "./EmptyState";
import { useToast } from "./Toast";
import { Tag as TagIcon } from "lucide-react";

/**
 * Drawer-based CRUD for the tenant's customer-tag taxonomy. Used both
 * from Settings → Custom fields → Customers (top-right "Manage tags"
 * button) and inline from the Scope tab on a customer custom field.
 */
export function CustomerTagsManager({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const q = useCustomerTags();
  const tags = q.data?.tags ?? [];

  const [newName, setNewName] = useState("");
  const [pendingDelete, setPendingDelete] = useState<CustomerTag | null>(null);

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ["customer.tags"] });

  const create = useMutation({
    mutationFn: (name: string) => createCustomerTag({ name }),
    onSuccess: () => {
      invalidate();
      setNewName("");
      toast.success("Tag added");
    },
    onError: (e) => toast.error("Couldn't add tag", (e as Error).message),
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteCustomerTag(id),
    onSuccess: () => {
      invalidate();
      setPendingDelete(null);
      toast.success("Tag deleted");
    },
    onError: (e) => {
      toast.error("Couldn't delete tag", (e as Error).message);
      setPendingDelete(null);
    },
  });

  const submit = () => {
    const name = newName.trim();
    if (!name) return;
    create.mutate(name);
  };

  return (
    <>
      <Drawer open={open} onClose={onClose} width="md">
        <DrawerHeader title="Manage tags" subtitle="Tenant-wide taxonomy. Used to scope custom fields and segment clients." onClose={onClose} />
        <DrawerBody className="px-5 py-5 space-y-4">
          <div className="flex gap-2">
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") submit();
              }}
              placeholder="e.g. VIP, Wholesale, Wedding"
              className="flex-1"
            />
            <Button onClick={submit} disabled={!newName.trim() || create.isPending}>
              <Plus className="h-3.5 w-3.5 mr-1.5" />
              {create.isPending ? "Adding…" : "Add"}
            </Button>
          </div>

          {tags.length === 0 ? (
            <EmptyState
              bare
              icon={TagIcon}
              title="No tags yet"
              description="Add a tag above to start grouping clients."
            />
          ) : (
            <ul className="rounded-lg border bg-card divide-y">
              {tags.map((t) => (
                <TagRow
                  key={t.id}
                  tag={t}
                  onDelete={() => setPendingDelete(t)}
                  onRename={async (name) => {
                    try {
                      await updateCustomerTag(t.id, { name });
                      invalidate();
                      toast.success("Tag renamed");
                    } catch (e) {
                      toast.error("Couldn't rename", (e as Error).message);
                    }
                  }}
                />
              ))}
            </ul>
          )}
        </DrawerBody>
        <DrawerFooter>
          <Button variant="outline" onClick={onClose}>
            Done
          </Button>
        </DrawerFooter>
      </Drawer>

      <ConfirmDialog
        open={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        onConfirm={() => pendingDelete && remove.mutate(pendingDelete.id)}
        title={`Delete "${pendingDelete?.name}"?`}
        description="Clients tagged with this label will lose the tag. Custom fields scoped to it stay defined but lose this entry from their tag-scope list."
        confirmLabel="Delete tag"
        pending={remove.isPending}
      />
    </>
  );
}

function TagRow({
  tag,
  onRename,
  onDelete,
}: {
  tag: CustomerTag;
  onRename: (name: string) => Promise<void>;
  onDelete: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(tag.name);

  const commit = async () => {
    const name = draft.trim();
    if (!name || name === tag.name) {
      setEditing(false);
      return;
    }
    await onRename(name);
    setEditing(false);
  };

  return (
    <li className="px-4 py-2.5 flex items-center gap-3">
      {editing ? (
        <Input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") commit();
            if (e.key === "Escape") {
              setDraft(tag.name);
              setEditing(false);
            }
          }}
          className="h-8 flex-1"
        />
      ) : (
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="flex-1 text-left text-sm hover:underline"
        >
          {tag.name}
        </button>
      )}
      <button
        type="button"
        onClick={onDelete}
        className="h-8 w-8 inline-flex items-center justify-center rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10"
        aria-label={`Delete ${tag.name}`}
      >
        <Trash2 className="h-4 w-4" />
      </button>
    </li>
  );
}
